// Aces & Deuces runner. Per-hole side game settled head-to-head
// against the rest of the field.
//
// Per-hole rule (applied to gross or net scores per the option):
//   • Outright LOW score (Ace)  → that player wins 1 point from each
//                                 other scored player on the hole.
//   • Outright HIGH score (Deuce) → that player loses 1 point to each
//                                   other scored player on the hole.
//   • Ties for low or high produce NO movement — keeps the math
//     symmetric and zero-sum without a tunable split option.
//   • Need at least two scored players for either award to fire.
//
// Settlement modes:
//   • Bet per point (Nassau unticked). Each ace/deuce point translates
//     to pointBet currency moving between the players. A player's per-
//     category bucket carries their NET cash from THAT category
//     (own gains + losses against opponents' events of the same kind).
//   • Nassau. Three pots — front 9, back 9, overall 18 — sized
//     `players.length × bet`. Highest segment-point total wins each
//     pot; ties split. Per-category buckets stay at 0 (segment pots
//     settle on `front` / `back` / `overall` instead).
//
// Output shape mirrors the Putting runner so the leaderboard can lay
// it out with the same two-row PUTTS-style header swap.

import { Course, Hole, Player, ScoreMap } from "../types";
import { HandicapMode, adjustHandicaps } from "../handicapAdjust";

// WHS-style course handicap — duplicated locally so this file stays
// standalone (no cross-runner imports).
function courseHandicap(
  handicapIndex: number | undefined,
  slope: number | undefined,
  rating: number | undefined,
  totalPar: number,
): number {
  if (handicapIndex == null) return 0;
  const slopeFactor = slope ? slope / 113 : 1;
  const ratingDiff = rating != null ? rating - totalPar : 0;
  return Math.round(handicapIndex * slopeFactor + ratingDiff);
}

function strokesOnHole(courseHcp: number, hole: Hole): number {
  if (courseHcp <= 0) return 0;
  const base = Math.floor(courseHcp / 18);
  const remainder = courseHcp % 18;
  return base + (hole.si <= remainder ? 1 : 0);
}

export type AcesScoringMode = "net" | "gross";

export interface AcesAndDeucesOptions {
  scoringMode?: AcesScoringMode;
  // Net-only handicap policy. "playOffLow" anchors the lowest player to
  // scratch; ignored when scoringMode is "gross".
  handicapMode?: HandicapMode;
  betPerPoint?: number;
  nassau?: boolean;
  overallBet?: number;
  frontNineBet?: number;
  backNineBet?: number;
  // Non-Nassau bet mode picker. When true (default) the round
  // settles head-to-head at `betPerPoint`. When false it settles as
  // a pot — each player antes `overallBet` and the highest ace/deuce
  // point total takes the pot.
  useBetPerPoint?: boolean;
}

export interface AcesEventBucket {
  // Own events in the category (1 = this player took the ace / wore
  // the deuce on that hole).
  count: number;
  // 1-based hole numbers where the player's own ace/deuce fired.
  holes: number[];
  // Bet-per-point: net cash flow in this category (own awards minus
  // payouts to others' awards of the same kind).
  // Nassau: 0 (pots settle on front/back/overall).
  winnings: number;
}

export interface AcesAndDeucesPayout {
  // Sum of intrinsic ace/deuce points (own awards minus shared
  // losses to others). Drives Nassau segment settlement; otherwise
  // informational.
  points: number;
  total: number;
  aces: AcesEventBucket;
  deuces: AcesEventBucket;
  overall: number;
  front: number;
  back: number;
}

export interface AcesAndDeucesResult {
  payouts: Record<string, AcesAndDeucesPayout>;
  nassauActive: boolean;
  hasAnyBet: boolean;
}

export interface AcesAndDeucesInput {
  course: Course;
  players: Player[];
  scores: ScoreMap;
  options: AcesAndDeucesOptions;
}

function emptyBucket(): AcesEventBucket {
  return { count: 0, holes: [], winnings: 0 };
}

export function runAcesAndDeuces(
  input: AcesAndDeucesInput,
): AcesAndDeucesResult {
  const { course, players, scores, options } = input;
  const scoringMode: AcesScoringMode =
    options.scoringMode === "gross" ? "gross" : "net";
  const nassau = !!options.nassau;
  // Non-Nassau bet-mode picker. Defaults to bet-per-point so legacy
  // rounds (saved before the toggle existed) keep their head-to-head
  // behaviour.
  const useBetPerPoint = options.useBetPerPoint !== false;
  const pointBet =
    !nassau && useBetPerPoint ? Number(options.betPerPoint) || 0 : 0;
  const playerBet =
    !nassau && !useBetPerPoint ? Number(options.overallBet) || 0 : 0;
  const overallBet = nassau ? Number(options.overallBet) || 0 : 0;
  const frontBet = nassau ? Number(options.frontNineBet) || 0 : 0;
  const backBet = nassau ? Number(options.backNineBet) || 0 : 0;

  // Course-handicap cache for net scoring. Apply the game's handicap
  // policy first ("playOffLow" anchors the lowest player to scratch);
  // adjustHandicaps returns a fresh array and leaves Player.handicap intact.
  const totalPar = course.holes.reduce((s, h) => s + h.par, 0);
  const tees = course.tees || [];
  const adjustedPlayers = adjustHandicaps(players, options.handicapMode);
  const courseHcpById: Record<string, number> = {};
  for (const p of adjustedPlayers) {
    const tee = tees.find((t) => t.id === p.tee);
    courseHcpById[p.id] = courseHandicap(
      p.handicap,
      tee?.slope,
      tee?.rating,
      totalPar,
    );
  }

  const scoreFor = (playerId: string, hole: Hole): number | null => {
    const sc = (scores[playerId] || {})[hole.number];
    if (!sc || typeof sc.gross !== "number") return null;
    if (scoringMode === "gross") return sc.gross;
    return sc.gross - strokesOnHole(courseHcpById[playerId], hole);
  };

  const payouts: Record<string, AcesAndDeucesPayout> = {};
  for (const p of players) {
    payouts[p.id] = {
      points: 0,
      total: 0,
      aces: emptyBucket(),
      deuces: emptyBucket(),
      overall: 0,
      front: 0,
      back: 0,
    };
  }

  // Per-(player, hole) intrinsic point contribution — used for Nassau
  // segment math and the Bet-per-point category totals.
  const segContrib: Record<string, Record<number, number>> = {};
  for (const p of players) {
    segContrib[p.id] = {};
    for (const h of course.holes) segContrib[p.id][h.number] = 0;
  }

  for (const hole of course.holes) {
    const entries: Array<{ id: string; score: number }> = [];
    for (const p of players) {
      const s = scoreFor(p.id, hole);
      if (s == null) continue;
      entries.push({ id: p.id, score: s });
    }
    if (entries.length < 2) continue;

    const min = Math.min(...entries.map((e) => e.score));
    const max = Math.max(...entries.map((e) => e.score));
    const aceWinners = entries.filter((e) => e.score === min);
    const deuceLosers = entries.filter((e) => e.score === max);

    // Ace: outright low only. Winner takes 1 from each other scored
    // player; the others each shed 1 to the winner. Tied low = no move.
    if (aceWinners.length === 1) {
      const winner = aceWinners[0].id;
      const opponents = entries.filter((e) => e.id !== winner);
      const opCount = opponents.length;

      payouts[winner].aces.count += 1;
      payouts[winner].aces.holes.push(hole.number);
      payouts[winner].points += opCount;
      segContrib[winner][hole.number] += opCount;

      for (const op of opponents) {
        payouts[op.id].points -= 1;
        segContrib[op.id][hole.number] -= 1;
      }

      if (!nassau && pointBet > 0) {
        payouts[winner].aces.winnings += opCount * pointBet;
        for (const op of opponents) {
          payouts[op.id].aces.winnings -= pointBet;
        }
      }
    }

    // Deuce: outright high only. Loser pays 1 to each other scored
    // player. Tied high = no move.
    if (deuceLosers.length === 1) {
      const loser = deuceLosers[0].id;
      const opponents = entries.filter((e) => e.id !== loser);
      const opCount = opponents.length;

      payouts[loser].deuces.count += 1;
      payouts[loser].deuces.holes.push(hole.number);
      payouts[loser].points -= opCount;
      segContrib[loser][hole.number] -= opCount;

      for (const op of opponents) {
        payouts[op.id].points += 1;
        segContrib[op.id][hole.number] += 1;
      }

      if (!nassau && pointBet > 0) {
        payouts[loser].deuces.winnings -= opCount * pointBet;
        for (const op of opponents) {
          payouts[op.id].deuces.winnings += pointBet;
        }
      }
    }
  }

  if (nassau) {
    const segPoints = (
      pid: string,
      inSeg: (h: number) => boolean,
    ): number => {
      let total = 0;
      for (const h of course.holes) {
        if (inSeg(h.number)) total += segContrib[pid][h.number] || 0;
      }
      return total;
    };

    const settle = (
      segFor: (pid: string) => number,
      bet: number,
      bucket: "overall" | "front" | "back",
    ) => {
      if (bet <= 0) return;
      const seg = players.map((p) => ({ id: p.id, pts: segFor(p.id) }));
      if (seg.length === 0) return;
      const maxPts = Math.max(...seg.map((s) => s.pts));
      const winners = seg.filter((s) => s.pts === maxPts);
      const pot = seg.length * bet;
      const share = pot / winners.length;
      for (const sp of seg) {
        payouts[sp.id][bucket] +=
          sp.pts === maxPts ? share - bet : -bet;
      }
    };

    settle((pid) => segPoints(pid, () => true), overallBet, "overall");
    settle(
      (pid) => segPoints(pid, (h) => h >= 1 && h <= 9),
      frontBet,
      "front",
    );
    settle(
      (pid) => segPoints(pid, (h) => h >= 10 && h <= 18),
      backBet,
      "back",
    );

    for (const p of players) {
      const pay = payouts[p.id];
      pay.total = pay.overall + pay.front + pay.back;
    }
  } else if (pointBet > 0) {
    // Bet-per-point: total is the sum of own + others' transfers
    // accumulated above into the per-category buckets.
    for (const p of players) {
      const pay = payouts[p.id];
      pay.total = pay.aces.winnings + pay.deuces.winnings;
    }
  } else if (playerBet > 0) {
    // Bet-per-player: each player antes playerBet; the highest
    // intrinsic point total takes the pot (ties split). Per-category
    // bucket cash stays at 0 — the round nets out on .total only,
    // matching how Putting's bet-per-player branch behaves.
    const seg = players.map((p) => ({ id: p.id, pts: payouts[p.id].points }));
    if (seg.length > 0) {
      const maxPts = Math.max(...seg.map((s) => s.pts));
      const winners = seg.filter((s) => s.pts === maxPts);
      const pot = seg.length * playerBet;
      const share = pot / winners.length;
      for (const sp of seg) {
        payouts[sp.id].total +=
          sp.pts === maxPts ? share - playerBet : -playerBet;
      }
    }
  }

  return {
    payouts,
    nassauActive: nassau,
    hasAnyBet:
      pointBet > 0 ||
      playerBet > 0 ||
      overallBet > 0 ||
      frontBet > 0 ||
      backBet > 0,
  };
}
